import { FC } from 'react';
import { useQuery } from 'react-query';
import { Form } from '@/components/form';
import FormButtons from '@/components/form/form-buttons';
import useFormHandle from '@/hooks/use-form-handle.hook';
import useToast from '@/hooks/use-toast.hook';
import { PolicyModel } from '@/lib/models/policy-model/policy.model';
import { QueryKey } from '@/lib/query-client';
import { getScopedPolicyWithParents, patchPolicy } from '@/lib/services/policy.service';
import useFeature from '@/hooks/use-feature.hook';
import PageFeatureToast from '@/components/page-feature-toast';
import { cloneDeep } from 'lodash';
import { FieldError } from '@/lib/services';
import FormSection from '@/components/form/form-section';
import { Grid } from '@mui/material';
import InputControl from '@/components/form/input-control';
import AllowWarningToast from './allow-warning-toast';
import { PublicServiceBlockOptions } from '@/lib/models/policy-model/public-services';
import ServiceControls from '@/components/form/service-controls';
import RerouteControls from '@/components/form/reroute-controls';
import FormSectionSubtitle from '@/components/form/form-section/form-section-subtitle';

const fields = ['publicServices'];
const formModel = new PolicyModel();

interface Props {
  policyId: string;
}

const ServiceAccessForm: FC<Props> = ({ policyId }) => {
  const { errorToast, toast } = useToast();
  const { canChangePolicy, canViewReroute } = useFeature();

  const query = useQuery([QueryKey.ScopedPolicyWithParents, policyId], async () =>
    getScopedPolicyWithParents(policyId)
  );

  const { data, refetch } = query;
  const [policy] = data || [];
  const { isDefault: isDefaultPolicy } = policy || { isDefault: false };

  const formHandle = useFormHandle({
    initialValues: formModel,
    validationSchema: formModel.schemaSlice(fields),
    onSubmit: async (values, { setSubmitting, resetForm, setFieldError }) => {
      const setFieldErrors = (fieldErrors: FieldError[]) => {
        fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
      };

      const updateValues = cloneDeep(values);

      const { patched, error, fieldErrors } = await patchPolicy(
        policyId,
        updateValues.propSlice(fields)
      );

      setSubmitting(false);

      if (patched) {
        resetForm({ values });
        refetch();
        toast('The policy was updated');
        return;
      }

      if (fieldErrors) {
        setFieldErrors(fieldErrors);
        return;
      }

      errorToast(error || 'The policy was not updated');
    },
  });

  const { values } = formHandle;
  const { targetsPublicServices } = values;

  const blockAction = values.publicServices?.blockAction;
  const hasRedirectUrl = blockAction === 'redirect-url';

  return (
    <Form formHandle={formHandle} query={query}>
      <PageFeatureToast featureId="change-policy" can={canChangePolicy} />

      <FormSection
        title="Catch-all Service Action"
        scrollId="catchall-service-action"
        disabled={targetsPublicServices}
      >
        <FormSectionSubtitle>
          For requests to services that are not explicitly allowed or blocked by scope, choose how
          to handle the request.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          {targetsPublicServices && (
            <Grid item xs={12}>
              <AllowWarningToast
                variant="acl"
                message="WARNING: This setting has no effect, since this policy either allows or blocks specific services."
              />
            </Grid>
          )}

          <Grid item xs={5}>
            <InputControl
              name="publicServices"
              type="policy-public-action-select"
              formHandle={formHandle}
              readonly={!canChangePolicy}
              disabled={targetsPublicServices}
              withNotSet={!isDefaultPolicy}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection title="Service Block Action" scrollId="service-block-action">
        <FormSectionSubtitle>
          When access to a service is blocked in the browser, choose whether users are shown a
          no-access page, or redirected to either the Portal or a custom URL.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={5}>
            <InputControl
              name="publicServices.blockAction"
              type="select"
              options={PublicServiceBlockOptions}
              formHandle={formHandle}
              readonly={!canChangePolicy}
              withNotSet={!isDefaultPolicy}
            />
          </Grid>
          <Grid item xs={6} justifyContent="center">
            {hasRedirectUrl && (
              <InputControl
                label="Redirect URL"
                name="publicServices.redirectUrl"
                formHandle={formHandle}
                readonly={!canChangePolicy}
              />
            )}
          </Grid>
        </Grid>
      </FormSection>

      <ServiceControls
        formHandle={formHandle}
        readonly={!canChangePolicy}
        policy={policy!}
        withNotSet={!isDefaultPolicy}
      />

      {canViewReroute && <RerouteControls formHandle={formHandle} readonly={!canChangePolicy} />}

      <FormButtons formHandle={formHandle} readonly={!canChangePolicy} />
    </Form>
  );
};

export default ServiceAccessForm;
